import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import groupBy from 'lodash/groupBy';
import countBy from 'lodash/countBy';
import {default as lSize} from 'lodash/size';

import colors from 'styles/_colors.css';
import styles from './FilterableTabs.css';

const cx = classnames.bind(styles);

class FilterableTabs extends Component {
  static propTypes = {
    children: PropTypes.func,
    className: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    primaryKey: PropTypes.string.isRequired,
    secondaryKey: PropTypes.string,
    primaryLabel: PropTypes.string,
    secondaryLabel: PropTypes.string,
    withoutLabels: PropTypes.bool,
    size: PropTypes.oneOf([
      's',
      'm',
      'l'
    ]),
    background: PropTypes.oneOf([
      'black',
      'grey',
      'red',
      'dark-red',
      'dark-blue',
      'heavy-blue',
      'dark-purple',
      'purple'
    ])
  };

  static defaultProps = {
    primaryLabel: 'content type',
    secondaryLabel: 'year',
    size: 'm',
  };

  state = {
    primaryKey: null,
    secondaryKey: null,
    items: null,
    active_tab: null,
    active_filter: null,
    results: null,
    tab_keys: null
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.primaryKey !== nextProps.primaryKey && prevState.secondaryKey !== nextProps.secondaryKey) {
      const { primaryKey, secondaryKey, items } = nextProps;
      const fetchedData = groupBy(items, primaryKey);
      const keys = Object.keys(fetchedData);

      if (keys.length > 3) {
        throw Error(`Filterable tabs a max of 3 tabs. Current number is: ${keys.length}.`);
      }

      return {
        primaryKey: primaryKey,
        secondaryKey: secondaryKey,
        items: fetchedData,
        active_tab: keys[0],
        results: fetchedData[keys[0]],
        tab_keys: keys
      };
    }
    return null;
  }

  handleTabClick = e => {
    e.preventDefault();
    const activeTab = e.currentTarget.dataset.tab;
    const results = this.fetchTabItems(activeTab);

    this.setState({ active_tab: activeTab, active_filter: null, results });
  }

  handleFilterClick = e => {
    e.preventDefault();
    let activeFilter = e.currentTarget.dataset.filter;
    activeFilter = activeFilter === 'all' ? null : activeFilter;

    const results = this.fetchFilterItems(activeFilter);

    this.setState({
      active_filter: activeFilter,
      results
    });
  }

  fetchTabItems = (tab) => this.state.items[tab];

  fetchFilterItems (filter) {
    const { active_tab, secondaryKey, items } = this.state;
    const filters = groupBy(items[active_tab], secondaryKey);

    return filter ? filters[filter] : items[active_tab];
  }

  filterCounter (filter) {
    const { active_tab, items, secondaryKey } = this.state;
    const count = countBy(items[active_tab], (item) => item[secondaryKey]);

    return count[filter];
  }

  renderFilters = () => {
    const { background, primaryLabel, secondaryLabel } = this.props;

    return (
      <Fragment>
        <p className={cx( 'label', { background } )}>{primaryLabel}</p>
        <p className={cx( 'label', { background } )}>{secondaryLabel}</p>
      </Fragment>
    );
  }

  renderTabList = () => {
    const { active_tab, tab_keys, items } = this.state;
    const { background, size, secondaryKey } = this.props;

    return (
      <ul className={cx('filters', { full: !secondaryKey })}>
        {tab_keys.map((tab, index) => {
          const itemCount = lSize(items[tab]);
          const classes = cx(
            size,
            'filter',
            {
              'active': active_tab === tab,
              background
            }
          );
          return (
            <li className={classes} key={`tab-${index}`}>
              <a
                onClick={this.handleTabClick}
                href="#"
                data-tab={tab}
                role={'tab'}
              >
                {tab}
                <sup>
                  {size === 's' ? `(${itemCount})` : itemCount}
                </sup>
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  renderFilterAll = () =>  {
    const { items, active_tab, active_filter } = this.state;
    const { size, background } = this.props;
    const itemCount = lSize(items[active_tab]);

    return (
      <li className={cx(size, 'filter', { 'active': !active_filter, background })} key={'filter-all'}>
        <a onClick={this.handleFilterClick} href="#" data-filter={'all'} role={'filter'}>
          All
          <sup>
            {size === 's' ? `(${itemCount})` : itemCount}
          </sup>
        </a>
      </li>
    );
  }

  renderFilterList = () => {
    const { items, active_tab, active_filter, secondaryKey } = this.state;
    const { size, background } = this.props;
    const filters = Object.keys(groupBy(items[active_tab], secondaryKey));

    return (
      <ul className={styles.filters}>
        {lSize(items[active_tab]) > 1 ? this.renderFilterAll() : null}
        {filters.map((filter, index) => {
          const filterCount = this.filterCounter(filter);
          const classes = cx(
            size,
            'filter',
            {
              'active': active_filter && active_filter === filter,
              background
            }
          );
          return (
            <li className={classes} key={`filter-${index}`}>
              <a
                onClick={this.handleFilterClick}
                href="#"
                data-filter={filter}
                role={'filter'}
              >
                {filter}
                <sup>
                  {size === 's'
                  ? `(${filterCount})`
                  : filterCount}
                </sup>
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  render () {
    const { results } = this.state;
    const { background, children, className, secondaryKey, withoutLabels } = this.props;

    return (
      <div className={styles.container}>
        <div
          className={classnames(styles.header, className)}
          style={{ background: colors[background] }}
        >
          {!withoutLabels && this.renderFilters()}
          {this.renderTabList()}
          {secondaryKey && this.renderFilterList()}
        </div>
        {children(results)}
      </div>
    );
  }
}

export default FilterableTabs;



// WEBPACK FOOTER //
// ./src/uis/elements/Tabs/FilterableTabs/FilterableTabs.js